import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { BCSaaSPlanEntity } from '@bluecypress/bcsaas-entities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

interface PlanDisplay {
  plan: BCSaaSPlanEntity;
  isEditing: boolean;
  editForm: PlanEditForm;
}

interface PlanEditForm {
  name: string;
  description: string;
  level: number;
  monthlyPrice: number;
  annualPrice: number;
  status: 'Active' | 'Inactive';
}

@Component({
  standalone: false,
  selector: 'izzy-plans',
  templateUrl: './plans.component.html',
  styleUrls: ['./plans.component.css']
})
export class PlansComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() initialState: any = null;
  @Output() stateChange = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() interaction = new EventEmitter<any>();

  constructor() {}

  protected plans: BCSaaSPlanEntity[] = [];
  protected planDisplays: PlanDisplay[] = [];

  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';

  protected isCreating: boolean = false;
  protected createForm: PlanEditForm = this.getEmptyForm();

  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getEmptyForm(): PlanEditForm {
    return {
      name: '',
      description: '',
      level: 0,
      monthlyPrice: 0,
      annualPrice: 0,
      status: 'Active'
    };
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const rv = new RunView();
      const result = await rv.RunView<BCSaaSPlanEntity>({
        EntityName: 'BC: Plans',
        OrderBy: 'Level ASC'
      });
      this.plans = result.Results;
      this.planDisplays = this.plans.map(p => ({
        plan: p,
        isEditing: false,
        editForm: this.planToForm(p)
      }));

      // Emit default status (plans don't have a health indicator)
      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading plans:', error);
      this.errorMessage = 'Failed to load plans.';
    } finally {
      this.isLoading = false;
    }
  }

  private planToForm(plan: BCSaaSPlanEntity): PlanEditForm {
    return {
      name: plan.Name,
      description: plan.Description || '',
      level: plan.Level || 0,
      monthlyPrice: plan.MonthlyPrice || 0,
      annualPrice: plan.AnnualPrice || 0,
      status: plan.Status as 'Active' | 'Inactive'
    };
  }

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.getEmptyForm();
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.createForm = this.getEmptyForm();
  }

  async saveCreate(): Promise<void> {
    if (!this.createForm.name.trim()) {
      this.errorMessage = 'Plan name is required.';
      return;
    }

    this.isSaving = true;
    try {
      const md = new Metadata();
      const newPlan = await md.GetEntityObject<BCSaaSPlanEntity>('BC: Plans');
      newPlan.Name = this.createForm.name.trim();
      newPlan.Description = this.createForm.description.trim() || null;
      newPlan.Level = this.createForm.level;
      newPlan.MonthlyPrice = this.createForm.monthlyPrice;
      newPlan.AnnualPrice = this.createForm.annualPrice;
      newPlan.Status = this.createForm.status;

      await newPlan.Save();
      await this.loadData();
      this.isCreating = false;
      this.showSuccess('Plan created');
    } catch (error) {
      console.error('Error creating plan:', error);
      this.errorMessage = 'Failed to create plan.';
    } finally {
      this.isSaving = false;
    }
  }

  startEdit(display: PlanDisplay): void {
    display.isEditing = true;
    display.editForm = this.planToForm(display.plan);
  }

  cancelEdit(display: PlanDisplay): void {
    display.isEditing = false;
  }

  async saveEdit(display: PlanDisplay): Promise<void> {
    this.isSaving = true;
    try {
      display.plan.Name = display.editForm.name.trim();
      display.plan.Description = display.editForm.description.trim() || null;
      display.plan.Level = display.editForm.level;
      display.plan.MonthlyPrice = display.editForm.monthlyPrice;
      display.plan.AnnualPrice = display.editForm.annualPrice;
      display.plan.Status = display.editForm.status;

      await display.plan.Save();
      display.isEditing = false;
      this.showSuccess('Plan updated');
    } catch (error) {
      console.error('Error updating plan:', error);
      this.errorMessage = 'Failed to update plan.';
    } finally {
      this.isSaving = false;
    }
  }

  confirmDelete(display: PlanDisplay): void {
    this.showConfirmDialog(
      'Delete Plan',
      `Are you sure you want to delete the "${display.plan.Name}" plan? Organizations on this plan will need to be migrated.`,
      async () => {
        this.isSaving = true;
        try {
          await display.plan.Delete();
          await this.loadData();
          this.showSuccess('Plan deleted');
        } catch (error) {
          console.error('Error deleting plan:', error);
          this.errorMessage = 'Failed to delete plan.';
        } finally {
          this.isSaving = false;
        }
      }
    );
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Pending': return 'status-pending';
      case 'Disabled': return 'status-disabled';
      default: return '';
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }

  protected showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadPlansComponent(): void {}
